import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { RequiredAction, Suggestion } from '../../interfaces/review-answer.interface';
import { ReviewFeedback } from '../../interfaces/review-feedback.interface';
import { ReviewFeedbackChange } from '../review-feedback/review-feedback.component';
import {
  formatSeverityLabel,
  formatSonarIssueType,
  formatSonarSeverity,
  severityColor,
  severityPriority,
  toTitle
} from '../../utils/severity.util';

export interface Annotation {
  line: number;
  type: 'action' | 'suggestion';
  findingId?: string;
  findingKind: 'required_action' | 'suggestion';
  severity?: string;
  severityLabel?: string;
  description: string;
  detail?: string;
  severityColor: 'danger' | 'warning' | 'info';
  priority: 1 | 2 | 3 | 4;
  // Campos Sonar — exibidos com rótulo igual ao VS Code
  sonarRule?: string;
  sonarSeverity?: string;
  sonarIssueType?: string;
  category?: string;
  categoryLabel?: string;
}

@Component({
  selector: 'app-annotations-panel',
  templateUrl: './annotations-panel.component.html',
  styleUrls: ['./annotations-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnotationsPanelComponent implements OnChanges, OnDestroy {
  @Input() requiredActions: RequiredAction[] = [];
  @Input() suggestions: Suggestion[] = [];
  @Input() activeLine: number | null = null;
  @Input() showActions = true;
  @Input() showSuggestions = true;
  @Input() resourceType = '';
  @Input() resourceId = '';
  @Input() answerArtifactId?: number;
  @Input() feedbackByFindingId = new Map<string, ReviewFeedback>();
  /** Avaliações de outros usuários por achado, só para leitura. */
  @Input() otherFeedbackByFindingId = new Map<string, ReviewFeedback[]>();
  @Output() annotationClicked = new EventEmitter<number>();
  @Output() feedbackChanged = new EventEmitter<ReviewFeedbackChange>();
  /** Avaliação de outro usuário removida por admin, para a tela tirá-la da lista. */
  @Output() otherFeedbackRemoved = new EventEmitter<ReviewFeedback>();
  @Output() showAllRequested = new EventEmitter<void>();
  @ViewChild('annotationsList') annotationsListRef?: ElementRef<HTMLElement>;

  annotations: Annotation[] = [];
  copiedSet = new Set<string>();
  private scrollToActiveFrame: number | null = null;
  private copyResetTimeouts = new Set<number>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.buildAnnotations();
    if (changes['activeLine'] || changes['requiredActions'] || changes['suggestions']) {
      this.scheduleScrollToActive();
    }
  }

  ngOnDestroy(): void {
    if (this.scrollToActiveFrame != null) {
      cancelAnimationFrame(this.scrollToActiveFrame);
      this.scrollToActiveFrame = null;
    }
    this.copyResetTimeouts.forEach(id => clearTimeout(id));
    this.copyResetTimeouts.clear();
  }

  private buildAnnotations(): void {
    const actions: Annotation[] = this.showActions
      ? (this.requiredActions ?? [])
          .filter(a => this.hasLine(a.line))
          .map(a => ({
            line: a.line!,
            type: 'action' as const,
            findingId: a.id,
            findingKind: 'required_action' as const,
            severity: a.severity,
            severityLabel: formatSeverityLabel(a.severity),
            description: a.description ?? '',
            detail: a.action,
            severityColor: severityColor(a.severity),
            priority: severityPriority(a.severity),
            sonarRule: a.sonar_rule_code,
            sonarSeverity: formatSonarSeverity(a.sonar_severity) || undefined,
            sonarIssueType: formatSonarIssueType(a.sonar_issue_type) || undefined
          }))
      : [];

    const suggestions: Annotation[] = this.showSuggestions
      ? (this.suggestions ?? [])
          .filter(s => this.hasLine(s.line))
          .map(s => ({
            line: s.line!,
            type: 'suggestion' as const,
            findingId: s.id,
            findingKind: 'suggestion' as const,
            description: s.description ?? '',
            detail: s.benefit,
            severityColor: 'info' as const,
            priority: 4 as const,
            category: s.category,
            categoryLabel: toTitle(s.category)
          }))
      : [];

    this.annotations = [...actions, ...suggestions].sort((a, b) => {
      if (a.line !== b.line) { return a.line - b.line; }
      if (a.priority !== b.priority) { return a.priority - b.priority; }
      if (a.type !== b.type) { return a.type === 'action' ? -1 : 1; }
      return 0;
    });
  }

  get totalFindingsCount(): number {
    return (this.requiredActions?.length ?? 0) + (this.suggestions?.length ?? 0);
  }

  get actionsWithLineCount(): number {
    return (this.requiredActions ?? []).filter(action => this.hasLine(action.line)).length;
  }

  get suggestionsWithLineCount(): number {
    return (this.suggestions ?? []).filter(suggestion => this.hasLine(suggestion.line)).length;
  }

  get findingsWithLineCount(): number {
    return this.actionsWithLineCount + this.suggestionsWithLineCount;
  }

  get visibleFindingsWithLineCount(): number {
    return (this.showActions ? this.actionsWithLineCount : 0)
      + (this.showSuggestions ? this.suggestionsWithLineCount : 0);
  }

  get hasHiddenFindingsWithLine(): boolean {
    return this.findingsWithLineCount > 0 && this.visibleFindingsWithLineCount === 0;
  }

  get emptyStateLabel(): string {
    if (this.hasHiddenFindingsWithLine) {
      return 'Cards ocultos';
    }
    if (this.totalFindingsCount > 0 && this.findingsWithLineCount === 0) {
      return 'Ações e sugestões';
    }
    return 'Ações e sugestões';
  }

  get emptyStateValue(): string {
    if (this.hasHiddenFindingsWithLine) {
      return 'Todos os cards com número de linha estão ocultos pelos filtros.';
    }
    if (this.totalFindingsCount > 0 && this.findingsWithLineCount === 0) {
      return 'Há achados neste review, mas nenhum possui número de linha disponível.';
    }
    return 'Nenhuma ação ou sugestão encontrada.';
  }

  requestShowAll(): void {
    this.showAllRequested.emit();
  }

  navigateTo(annotation: Annotation): void {
    this.annotationClicked.emit(annotation.line);
  }

  private hasLine(line: unknown): boolean {
    return typeof line === 'number' && Number.isFinite(line) && line > 0;
  }

  private annotationKey(annotation: Annotation): string {
    if (annotation.findingId) {
      return `${annotation.type}:${annotation.findingId}`;
    }
    return `${annotation.type}:${annotation.line}:${annotation.priority}:${annotation.description}:${annotation.detail ?? ''}`;
  }

  trackByAnnotation(_index: number, annotation: Annotation): string {
    if (annotation.findingId) {
      return `${annotation.type}:${annotation.findingId}`;
    }
    return `${annotation.type}:${annotation.line}:${annotation.priority}:${annotation.description}:${annotation.detail ?? ''}`;
  }

  annotationTypeLabel(annotation: Annotation): string {
    return annotation.type === 'suggestion' ? 'Sugestão' : 'Ação necessária';
  }

  /** Subtítulo do cabeçalho: "Severidade alta" na ação, a categoria na sugestão. */
  annotationQualifier(annotation: Annotation): string {
    if (annotation.type === 'action') {
      return annotation.severityLabel ? `Severidade ${annotation.severityLabel.toLowerCase()}` : '';
    }
    return annotation.categoryLabel || annotation.category || '';
  }

  copyAnnotation(ann: Annotation): void {
    const lines: string[] = [];
    const typeLabel = ann.type === 'suggestion' ? '💡 Sugestão' : '⚠ Ação necessária';
    lines.push(`${typeLabel} — L${ann.line}`);
    if (ann.severityLabel) { lines.push(`Severidade: ${ann.severityLabel}`); }
    if (ann.categoryLabel || ann.category) { lines.push(`Categoria: ${ann.categoryLabel ?? ann.category}`); }
    if (ann.sonarRule) { lines.push(`Regra Sonar: ${ann.sonarRule}`); }
    if (ann.sonarSeverity) { lines.push(`Severidade Sonar: ${ann.sonarSeverity}`); }
    if (ann.sonarIssueType) { lines.push(`Tipo Sonar: ${ann.sonarIssueType}`); }
    if (ann.description) { lines.push('', 'Problema:', ann.description); }
    if (ann.detail) {
      lines.push('', ann.type === 'suggestion' ? 'Benefício:' : 'Ação:', ann.detail);
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      const key = this.annotationKey(ann);
      this.copiedSet.add(key);
      this.cdr.markForCheck();
      const timeoutId = window.setTimeout(() => {
        this.copiedSet.delete(key);
        this.copyResetTimeouts.delete(timeoutId);
        this.cdr.markForCheck();
      }, 2000);
      this.copyResetTimeouts.add(timeoutId);
    });
  }

  isCopied(ann: Annotation): boolean {
    return this.copiedSet.has(this.annotationKey(ann));
  }

  annotationIcon(annotation: Annotation): string {
    return annotation.type === 'suggestion' ? '💡' : '⚠';
  }

  isActive(annotation: Annotation): boolean {
    return this.activeLine != null && annotation.line === this.activeLine;
  }

  /** Controles de feedback só aparecem quando o achado tem id e há answerArtifactId. */
  canShowFeedback(annotation: Annotation): boolean {
    return !!annotation.findingId && this.answerArtifactId != null;
  }

  feedbackFor(annotation: Annotation): ReviewFeedback | undefined {
    return annotation.findingId ? this.feedbackByFindingId.get(annotation.findingId) : undefined;
  }

  otherFeedbackFor(annotation: Annotation): ReviewFeedback[] {
    return annotation.findingId ? this.otherFeedbackByFindingId.get(annotation.findingId) ?? [] : [];
  }

  private scheduleScrollToActive(): void {
    if (typeof requestAnimationFrame === 'undefined') { return; }
    if (this.scrollToActiveFrame != null) {
      cancelAnimationFrame(this.scrollToActiveFrame);
    }
    this.scrollToActiveFrame = requestAnimationFrame(() => {
      this.scrollToActiveFrame = null;
      this.scrollToActive();
    });
  }

  private scrollToActive(): void {
    if (this.activeLine == null) { return; }
    const listEl = this.annotationsListRef?.nativeElement;
    if (!listEl) { return; }
    const activeEl = listEl.querySelector(`.annotation-widget[data-line="${this.activeLine}"]`) as HTMLElement | null;
    activeEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}
